import Backbone from 'backbone';
import React from 'react';

import {DigestAnalysis} from 'analyses/Analyses';
import DigestMenuTemplate from 'analyses/DigestMenuTemplate';
import {DigestTabState} from 'analyses/DigestTabState';
import DigestTabTemplate from 'analyses/DigestTabTemplate';
import FragmentRowTemplate, {FragmentRowProps} from 'analyses/FragmentRowTemplate';
import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {FragmentResult} from 'biojs/FragmentResult';
import {Sequence as BioJSSequence} from 'biojs/Sequence';
import * as selectioncontextmenu from 'components/SelectionContextMenu';
import {Sequence as ComponentSequence} from 'components/Sequence';
import EnzymeActivityTable from 'molbio/enzymes/EnzymeActivityTable';
import {BaseProvider} from 'molbio/enzymes/EnzymeConstants';
import EnzymeProviderSelector from 'molbio/enzymes/EnzymeProviderSelector';
import * as EnzymeUtil from 'molbio/enzymes/EnzymeUtil';
import * as sequtil from 'molbio/SeqUtil';
import notifications from 'notifications';
import {renderToDOM} from 'redux-stores/ReduxDOMRender';
import getTestProps from 'test/lib/cypress/getTestProps';

class OptionsView extends Backbone.View<DigestTabState> {
  name!: JQuery;
  sequence!: ComponentSequence;

  template!: () => string;

  static initClass(): void {
    this.prototype.tagName = 'div';
    this.prototype.className = 'navbar';
    this.prototype.template = DigestMenuTemplate;
  }

  override events(): Backbone.EventsHash {
    return {
      'click .save': 'save',
      'keypress .tab-name': 'checkEnter',
      'keyup .tab-name': 'setFromText',
    };
  }

  override initialize(): void {
    this.updateProvider = this.updateProvider.bind(this);
    this.sequence = this.model.get('sequence');
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.model.get('analysis'), 'change:provider', this.renderProviderSelector);
  }

  setFromText(): void {
    this.model.set('title', this.name.val().trim());
  }

  checkEnter(e: KeyboardEvent): void {
    if (e.which === 13) {
      this.save();
    }
  }

  save(): void {
    if (!this.model.get('sequence').allowFeatureEdits()) {
      return;
    }
    this.$('.save').button('loading');

    const analysis: DigestAnalysis = this.model.get('analysis');
    analysis.set('name', this.name.val().trim());
    const saveWord = analysis.isNew() ? 'saved' : 'updated';
    const createPromise = this.sequence.get('analyses').betterCreate(analysis);
    assert(createPromise !== false, 'Analysis create failed Backbone validation!');
    createPromise
      .always(() => this.$('.save').button('reset'))
      .done(() => {
        notifications.success(
          <>
            Digest <strong>{this.name.val()}</strong> successfully {saveWord}.
          </>
        );
        analytics.track('digest', 'saved_digest', {name: analysis.get('name')});
      })
      .fail(notifications.notifyError);
  }

  updateProvider(provider: BaseProvider): void {
    this.model.get('analysis').set({provider: provider.name});
  }

  renderProviderSelector(): void {
    const analysis = this.model.get('analysis');
    const allowFeatureEdits = this.sequence.allowFeatureEdits();

    const providers = EnzymeUtil.getProvidersForEnzymes(analysis.get('enzymes'));
    const selectedProvider = analysis.provider();

    renderToDOM(
      <EnzymeProviderSelector
        inline={true}
        providers={providers}
        value={selectedProvider}
        // eslint-disable-next-line @typescript-eslint/unbound-method
        onChange={allowFeatureEdits ? this.updateProvider : undefined}
      />,
      this.$el.find('.enzymes-providers-mount')[0]
    );
  }

  override render(): this {
    this.$el.html(this.template());
    this.renderProviderSelector();
    this.name = this.$el.find('.tab-name');
    if (this.sequence.allowFeatureEdits()) {
      // eslint-disable-next-line @typescript-eslint/unbound-method
      this.name.bind('keypress', this.setFromText);
    } else {
      this.name.prop('readonly', true);
      this.$('.save').addClass('disabled').text('Save (disabled in read-only mode)');
    }

    this.$('.save').attr({type: 'button', 'data-loading-text': 'Saving...'});

    this.name.val(this.model.get('analysis').get('name'));
    return this;
  }
}
OptionsView.initClass();

interface FragmentRowViewOptions extends Backbone.ViewOptions<BioJSSequence> {
  sequence: ComponentSequence;
}

class FragmentRowView extends Backbone.View<BioJSSequence> {
  sequence!: ComponentSequence;
  template!: (props: FragmentRowProps) => string;

  static initClass(): void {
    this.prototype.tagName = 'tr';
    this.prototype.template = FragmentRowTemplate;

    this.prototype.attributes = {
      ...this.prototype.attributes,
      ...getTestProps({component: 'FragmentRow', element: 'row'}),
    };
  }

  override events(): Backbone.EventsHash {
    return {
      mousedown: 'selectFragment',
      contextmenu: 'contextMenu',
    };
  }

  override initialize(options: FragmentRowViewOptions): void {
    this.sequence = options.sequence;
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.model, 'change:selected', this.handleSelectedChange);
  }

  contextMenu(e: JQueryEventObject): void {
    selectioncontextmenu.open(e, this.sequence, selectioncontextmenu.COPY_MENU);
  }

  selectFragment(): void {
    this.sequence.selectFragment(this.model);
  }

  handleSelectedChange(model: FragmentResult, selected: boolean): void {
    this.$el.toggleClass('active', selected);
  }

  override render(): this {
    const leftCutterOverhang = this.model.leftOverhang();
    const leftCutter = this.model.get('_cutter5');
    const leftCutterName = leftCutter != null ? leftCutter.name : 'None';
    const rightCutterOverhang = this.model.rightOverhang();
    const rightCutter = this.model.get('_cutter3');
    const rightCutterName = rightCutter != null ? rightCutter.name : 'None';
    const [start, end] = sequtil.modelRangeToDisplay(
      this.model.get('start'),
      this.model.get('end'),
      this.sequence.length(),
      this.sequence.startIndex()
    );
    this.$el.html(
      this.template({
        start: start!,
        end: end!,
        length: this.model.get('length'),
        leftCutterName,
        leftCutterOverhang,
        rightCutterName,
        rightCutterOverhang,
      })
    );
    if (this.model.get('selected')) {
      this.$el.addClass('active');
    }
    return this;
  }
}
FragmentRowView.initClass();

class TabView extends Backbone.View<DigestTabState> {
  rows!: Array<FragmentRowView>;
  sequence!: ComponentSequence;
  _sequence!: BioJSSequence;
  template!: () => string;

  static initClass(): void {
    this.prototype.template = DigestTabTemplate;
    this.prototype.className = 'buffer-tab';
  }

  override events(): Backbone.EventsHash {
    return {
      'click tr': 'selectFragment',
    };
  }

  override initialize(): void {
    this.sequence = this.model.get('sequence');
    this._sequence = this.sequence.get('_sequence');
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this._sequence, 'change:circular change:bases change:startIndex', this.render);
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.model, 'change:fragments', this.render);
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.model.get('analysis'), 'change:provider', this.renderEnzymes);
    this.rows = [];
  }

  renderEnzymes(): void {
    const provider = this.model.get('analysis').provider();
    const enzymeIds = this.model.get('analysis').get('enzymes');
    const enzymes = this.sequence.get('enzymes').getEnzymesJSON(enzymeIds);
    renderToDOM(<EnzymeActivityTable provider={provider} enzymes={enzymes} />, this.$el.find('.enzymes')[0]);
  }

  override render(): this {
    const enzymeIds = this.model.get('analysis').get('enzymes');
    const enzymes = this.sequence.get('enzymes').getEnzymesJSON(enzymeIds);

    this.$el.html(this.template());
    this.renderEnzymes();

    if (enzymes.length !== 0) {
      const fragments = this.model.get('analysis').get('fragments');
      for (const fragment of Array.from<BioJSSequence>(fragments.models)) {
        const rowViewOptions: FragmentRowViewOptions = {
          model: fragment,
          sequence: this.sequence,
        };
        const row = new FragmentRowView(rowViewOptions);
        this.rows.push(row);
        this.$el.find('.fragments-table tbody').append(row.render().el);
      }
    }
    return this;
  }

  override remove(): Backbone.View<DigestTabState> {
    for (const row of Array.from(this.rows)) {
      row.stopListening();
    }
    return super.remove();
  }
}
TabView.initClass();

export {OptionsView, TabView};
